import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  AUTH_SESSION_ENDED_EVENT,
  ApiError,
  api,
  clearAccessToken,
  getAccessToken,
} from './api/client';
import Layout from './components/Layout';
import Login from './components/Login';
import { ToastProvider } from './components/Toast';
import { MeProvider } from './context/MeContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { SupportModeProvider } from './context/SupportModeContext';
import ActivityPage from './pages/Activity';
import JobsDashboardPage from './pages/JobsDashboard';
import SearchExecutionsPage from './pages/SearchExecutions';
import BillingCancelPage from './pages/BillingCancel';
import BillingSuccessPage from './pages/BillingSuccess';
import BillingPage from './pages/Billing';
import BillingCustomerPlansPage from './pages/BillingCustomerPlans';
import BillingHistoryPage from './pages/BillingHistory';
import BillingInvoicesPage from './pages/BillingInvoices';
import TemplatesMarketplacePage from './pages/TemplatesMarketplace';
import TemplateDetailPage from './pages/TemplateDetail';
import TemplateInstallPage from './pages/TemplateInstall';
import TemplateFavoritesPage from './pages/TemplateFavorites';
import TemplateMyPage from './pages/TemplateMy';
import TemplateHistoryPage from './pages/TemplateHistory';
import ConfigPage from './pages/Config';
import Dashboard from './pages/Dashboard';
import ReleaseHealthPage from './pages/ReleaseHealth';
import SearchLinksPage from './pages/SearchLinks';
import SearchesPage from './pages/Searches';
import SourcesPage from './pages/Sources';
import SubscriptionsPage from './pages/Subscriptions';
import WorkspaceUsersPage from './pages/WorkspaceUsers';
import ManagementUsersPage from './pages/ManagementUsers';
import RecipientsPage from './pages/Recipients';
import AccountStatusPage from './pages/AccountStatus';
import ApprovalsPage from './pages/Approvals';
import BillingPlansPage from './pages/BillingPlans';
import BillingSubscriptionsPage from './pages/BillingSubscriptions';
import PaymentProvidersPage from './pages/PaymentProviders';
import UsagePage from './pages/Usage';
import OrganizationPage from './pages/Organization';
import OrganizationSettingsPage from './pages/OrganizationSettings';
import PlatformOrganizationsPage from './pages/PlatformOrganizationsPage';
import BillingAttentionPage from './pages/BillingAttention';
import AccountPage from './pages/Account';
import CustomerActivityPage from './pages/CustomerActivity';
import AdminBillingDashboardPage from './pages/adminBilling/AdminBillingDashboard';
import AdminBillingRevenuePage from './pages/adminBilling/AdminBillingRevenue';
import AdminBillingPaymentsPage from './pages/adminBilling/AdminBillingPayments';
import AdminBillingSubscriptionsPage from './pages/adminBilling/AdminBillingSubscriptions';
import AdminBillingPlansPage from './pages/adminBilling/AdminBillingPlans';
import AdminBillingProvidersPage from './pages/adminBilling/AdminBillingProviders';
import AdminBillingWebhooksPage from './pages/adminBilling/AdminBillingWebhooks';
import AdminBillingAttentionPage from './pages/adminBilling/AdminBillingAttention';
import AdminBillingExportsPage from './pages/adminBilling/AdminBillingExports';
import AdminBillingOrganizationsPage from './pages/adminBilling/AdminBillingOrganizations';
import AdminBillingOrganizationDetailPage from './pages/adminBilling/AdminBillingOrganizationDetail';
import RegisterPage from './pages/Register';
import InvitationAcceptPage from './pages/InvitationAccept';
import SupportModePage from './pages/SupportMode';
import MaintenancePage from './pages/Maintenance';
import WizardPage from './pages/Wizard';
import RegistrationConfirmedPage from './pages/RegistrationConfirmed';
import RestrictedAcceptTermsApp from './routing/RestrictedAcceptTermsApp';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import CookiesPage from './pages/legal/CookiesPage';
import './ui/theme/tokens.css';
import './ui/ui-components.css';
import './ui/ui-layout.css';
import './App.css';
import './styles/responsive-layout.css';
import './styles/admin-panels.css';
import './styles/dashboard-polish.css';

function ProtectedApp() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [termsGate, setTermsGate] = useState<boolean>(false);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setAuthed(false);
      return;
    }
    api.auth
      .me()
      .then((me) => {
        const required = Boolean((me as { termsAcceptanceRequired?: boolean }).termsAcceptanceRequired);
        setTermsGate(required);
        setAuthed(true);
      })
      .catch(() => {
        clearAccessToken();
        setAuthed(false);
      });
  }, []);

  useEffect(() => {
    if (!authed) return;
    const verifySession = () => {
      void api.auth.me().catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          clearAccessToken();
          setAuthed(false);
        }
      });
    };
    const intervalId = window.setInterval(verifySession, 15_000);
    window.addEventListener('focus', verifySession);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', verifySession);
    };
  }, [authed]);

  if (authed === null) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <span>Loading admin console…</span>
      </div>
    );
  }

  if (!authed) {
    return <Navigate to="/login" replace />;
  }

  if (termsGate) {
    return <Navigate to="/accept-terms" replace />;
  }

  return (
    <ToastProvider>
      <MeProvider>
        <WorkspaceProvider>
          <FeatureFlagProvider>
            <SupportModeProvider>
              <Layout />
            </SupportModeProvider>
          </FeatureFlagProvider>
        </WorkspaceProvider>
      </MeProvider>
    </ToastProvider>
  );
}

function AuthenticationLifecycleListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionEnded = () => navigate('/login', { replace: true });
    window.addEventListener(AUTH_SESSION_ENDED_EVENT, handleSessionEnded);
    return () => window.removeEventListener(AUTH_SESSION_ENDED_EVENT, handleSessionEnded);
  }, [navigate]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthenticationLifecycleListener />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/registration-confirmed" element={<RegistrationConfirmedPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/accept-terms" element={<RestrictedAcceptTermsApp />} />
        <Route element={<ProtectedApp />}>
          <Route path="account-status" element={<AccountStatusPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="billing/plans" element={<BillingCustomerPlansPage />} />
          <Route path="billing/history" element={<BillingHistoryPage />} />
          <Route path="billing/invoices" element={<BillingInvoicesPage />} />
          <Route path="workspace" element={<OrganizationPage />} />
          <Route path="workspace/members" element={<Navigate to="/users" replace />} />
          <Route path="workspace/settings" element={<OrganizationSettingsPage />} />
          <Route path="organizations/manage" element={<PlatformOrganizationsPage />} />
          <Route path="organization" element={<Navigate to="/workspace" replace />} />
          <Route path="organization/members" element={<Navigate to="/users" replace />} />
          <Route path="organization/settings" element={<Navigate to="/workspace/settings" replace />} />
          <Route path="billing/packages" element={<BillingPlansPage />} />
          <Route path="billing/attention" element={<BillingAttentionPage />} />
          <Route path="billing/subscriptions" element={<BillingSubscriptionsPage />} />
          <Route path="admin/billing" element={<AdminBillingDashboardPage />} />
          <Route path="admin/billing/revenue" element={<AdminBillingRevenuePage />} />
          <Route path="admin/billing/payments" element={<AdminBillingPaymentsPage />} />
          <Route path="admin/billing/subscriptions" element={<AdminBillingSubscriptionsPage />} />
          <Route path="admin/billing/plans" element={<AdminBillingPlansPage />} />
          <Route path="admin/billing/providers" element={<AdminBillingProvidersPage />} />
          <Route path="admin/billing/webhooks" element={<AdminBillingWebhooksPage />} />
          <Route path="admin/billing/attention" element={<AdminBillingAttentionPage />} />
          <Route path="admin/billing/exports" element={<AdminBillingExportsPage />} />
          <Route path="admin/billing/organizations" element={<AdminBillingOrganizationsPage />} />
          <Route path="admin/billing/organizations/:id" element={<AdminBillingOrganizationDetailPage />} />
          <Route path="payment-providers" element={<PaymentProvidersPage />} />
          <Route index element={<Dashboard />} />
          <Route path="setup" element={<WizardPage />} />
          <Route path="recipients" element={<RecipientsPage />} />
          <Route path="users" element={<WorkspaceUsersPage />} />
          <Route path="team" element={<Navigate to="/users" replace />} />
          <Route path="admin/platform-users" element={<ManagementUsersPage />} />
          <Route path="customer-activity" element={<CustomerActivityPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="invite/:token" element={<InvitationAcceptPage />} />
          <Route path="support" element={<SupportModePage />} />
          <Route path="searches" element={<SearchesPage />} />
          <Route path="templates" element={<TemplatesMarketplacePage />} />
          <Route path="templates/favorites" element={<TemplateFavoritesPage />} />
          <Route path="templates/my" element={<TemplateMyPage />} />
          <Route path="templates/history" element={<TemplateHistoryPage />} />
          <Route path="templates/install" element={<TemplateInstallPage />} />
          <Route path="templates/:id" element={<TemplateDetailPage />} />
          <Route path="searches/:id/links" element={<SearchLinksPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="config" element={<ConfigPage />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="jobs" element={<JobsDashboardPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="pipeline" element={<SearchExecutionsPage />} />
          <Route path="release-health" element={<ReleaseHealthPage />} />
          <Route path="billing/success" element={<BillingSuccessPage />} />
          <Route path="billing/cancel" element={<BillingCancelPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
